/**
 * usePermission Hook
 * 
 * Custom React hook for checking permissions in components.
 * Provides convenient methods to check user permissions for access control.
 */

import { useAppSelector } from '../store/hooks';
import { Action, Permission, Resource } from '@/shared/application/types/authTypes';
import * as permissionUtils from '@/shared/application/utils/permissionUtils';

interface UsePermissionReturn {
    /** All permissions the user has */
    permissions: Permission[];

    /** Check if user has a specific permission */
    hasPermission: (permission: Permission) => boolean;

    /** Check if user has any of the specified permissions */
    hasAnyPermission: (permissions: Permission[]) => boolean;

    /** Check if user has all of the specified permissions */
    hasAllPermissions: (permissions: Permission[]) => boolean;

    /** Check an arbitrary resource/action pair (generic form of canRead/canCreate/…) */
    can: (resource: Resource, action: Action) => boolean;

    /** Check if user can read a specific resource */
    canRead: (resource: Resource) => boolean;

    /** Check if user can create in a specific resource */
    canCreate: (resource: Resource) => boolean;

    /** Check if user can edit a specific resource */
    canEdit: (resource: Resource) => boolean;

    /** Check if user can delete from a specific resource */
    canDelete: (resource: Resource) => boolean;

    /** Get full access details for a resource */
    getResourceAccess: (resource: Resource) => {
        canRead: boolean;
        canCreate: boolean;
        canEdit: boolean;
        canDelete: boolean;
    };
}

/**
 * Hook to check permissions in components
 * 
 * @returns Object with permission checking functions
 * 
 * @example
 * ```tsx
 * import { Resource, Action, createPermission } from '@/shared/application/types/authTypes';
 * 
 * function Section2Page() {
 *   const { canEdit, canDelete, hasPermission } = usePermission();
 *   
 *   const canEditSection2 = canEdit(Resource.SECTION2);
 *   const canDeleteSection2 = canDelete(Resource.SECTION2);
 *   
 *   // Or using createPermission
 *   const hasEditPerm = hasPermission(createPermission(Resource.SECTION2, Action.EDIT));
 *   
 *   return (
 *     <div>
 *       {canEditSection2 && <button>Edit</button>}
 *       {canDeleteSection2 && <button>Delete</button>}
 *     </div>
 *   );
 * }
 * ```
 */
export function usePermission(): UsePermissionReturn {
    // Get permissions from Redux state
    const permissions = useAppSelector((state) => state.auth.permissions);

    return {
        permissions,

        hasPermission: (permission: Permission) =>
            permissionUtils.hasPermission(permissions, permission),

        hasAnyPermission: (requiredPermissions: Permission[]) =>
            permissionUtils.hasAnyPermission(permissions, requiredPermissions),

        hasAllPermissions: (requiredPermissions: Permission[]) =>
            permissionUtils.hasAllPermissions(permissions, requiredPermissions),

        can: (resource: Resource, action: Action) =>
            permissionUtils.can(permissions, resource, action),

        canRead: (resource: Resource) =>
            permissionUtils.canRead(permissions, resource),

        canCreate: (resource: Resource) =>
            permissionUtils.canCreate(permissions, resource),

        canEdit: (resource: Resource) =>
            permissionUtils.canEdit(permissions, resource),

        canDelete: (resource: Resource) =>
            permissionUtils.canDelete(permissions, resource),

        getResourceAccess: (resource: Resource) =>
            permissionUtils.getResourceAccess(permissions, resource),
    };
}
